import React, { useState, useEffect, useCallback } from 'react';
import {
    Box, Typography, Button, IconButton, TextField, Switch, FormControlLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    Dialog, DialogTitle, DialogContent, DialogActions, CircularProgress,
    Alert, Tooltip, Chip, InputAdornment, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    AccessAlarm as AlarmIcon,
    CheckCircle as ActiveIcon,
    Cancel as InactiveIcon,
    SwapHoriz as SwapIcon,
    ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import { pricingAPI, carTypesAPI } from '../../services/apiService';
import { formatTimeOnly } from '../../utils/dateHelpers';

const DAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const EMPTY_FEE = {
    name: '',
    zoneIds: '[]',
    startTime: '14:00',
    endTime: '18:30',
    fee: '',
    overridesOthers: false,
    isActive: true,
    matchMode: 'ZONE',
    originZoneIds: '[]',
    destinationZoneIds: '[]',
    bidirectional: false,
    carTypeIds: '[]',
    daysOfWeek: '[]',
};

const RushHourFeeManager = () => {
    const [fees, setFees] = useState([]);
    const [zones, setZones] = useState([]);
    const [carTypes, setCarTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FEE);
    // Zone selection state
    const [selectedZoneIds, setSelectedZoneIds] = useState([]);
    const [selectedOriginZoneIds, setSelectedOriginZoneIds] = useState([]);
    const [selectedDestZoneIds, setSelectedDestZoneIds] = useState([]);
    // Car type / day selection state (as int arrays)
    const [selectedCarTypeIds, setSelectedCarTypeIds] = useState([]);
    const [selectedDays, setSelectedDays] = useState([]); // int[]
    const [saving, setSaving] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            const [feeData, zoneData, carTypeData] = await Promise.all([
                pricingAPI.getRushHourFees(),
                pricingAPI.getZones(),
                carTypesAPI.getAll(),
            ]);
            setFees(feeData);
            setZones(zoneData.filter(z => !z.normalizeTo));
            setCarTypes(carTypeData);
            setError(null);
        } catch (err) {
            setError('Failed to load data: ' + (err.response?.data?.error || err.message));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);

    const parseIds = (json) => { try { return JSON.parse(json) || []; } catch { return []; } };

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY_FEE);
        setSelectedZoneIds([]);
        setSelectedOriginZoneIds([]);
        setSelectedDestZoneIds([]);
        setSelectedCarTypeIds([]);
        setSelectedDays([]);
        setDialogOpen(true);
    };

    const openEdit = (fee) => {
        setEditing(fee);
        setForm({
            name: fee.name,
            zoneIds: fee.zoneIds,
            startTime: fee.startTime,
            endTime: fee.endTime,
            fee: fee.fee.toString(),
            overridesOthers: fee.overridesOthers,
            isActive: fee.isActive,
            matchMode: fee.matchMode || 'ZONE',
            originZoneIds: fee.originZoneIds || '[]',
            destinationZoneIds: fee.destinationZoneIds || '[]',
            bidirectional: fee.bidirectional || false,
            carTypeIds: fee.carTypeIds || '[]',
            daysOfWeek: fee.daysOfWeek || '[]',
        });
        setSelectedZoneIds(parseIds(fee.zoneIds));
        setSelectedOriginZoneIds(parseIds(fee.originZoneIds));
        setSelectedDestZoneIds(parseIds(fee.destinationZoneIds));
        setSelectedCarTypeIds(parseIds(fee.carTypeIds));
        setSelectedDays(parseIds(fee.daysOfWeek));
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.name.trim()) { setError('Name is required'); return; }
        const feeAmount = parseFloat(form.fee);
        if (!feeAmount || feeAmount <= 0) { setError('Fee must be greater than 0'); return; }

        if (form.matchMode === 'ROUTE') {
            if (selectedOriginZoneIds.length === 0) { setError('ROUTE mode requires at least one origin zone'); return; }
            if (selectedDestZoneIds.length === 0) { setError('ROUTE mode requires at least one destination zone'); return; }
        }

        // "All selected" → send "[]" so future additions are included automatically
        const allCarTypesSelected = selectedCarTypeIds.length === carTypes.length;
        const allDaysSelected = selectedDays.length === 7;

        try {
            setSaving(true);
            const payload = {
                ...form,
                fee: feeAmount,
                zoneIds: JSON.stringify(selectedZoneIds),
                originZoneIds: JSON.stringify(selectedOriginZoneIds),
                destinationZoneIds: JSON.stringify(selectedDestZoneIds),
                carTypeIds: allCarTypesSelected ? '[]' : JSON.stringify(selectedCarTypeIds),
                daysOfWeek: allDaysSelected ? '[]' : JSON.stringify(selectedDays),
            };
            if (editing) {
                await pricingAPI.updateRushHourFee(editing.id, payload);
            } else {
                await pricingAPI.createRushHourFee(payload);
            }
            setDialogOpen(false);
            setSuccess(editing ? 'Fee updated' : 'Fee created');
            setTimeout(() => setSuccess(null), 3000);
            await fetchData();
        } catch (err) {
            setError('Save failed: ' + (err.response?.data?.error || err.message));
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (fee) => {
        try {
            await pricingAPI.deleteRushHourFee(fee.id);
            setDeleteConfirm(null);
            setSuccess('Fee deleted');
            setTimeout(() => setSuccess(null), 2000);
            await fetchData();
        } catch (err) {
            setError('Delete failed: ' + (err.response?.data?.error || err.message));
            setDeleteConfirm(null);
        }
    };

    const toggleId = (setter, id) =>
        setter(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

    const zoneNameById = (id) => zones.find(z => z.id === id)?.name || `Zone ${id}`;
    const carTypeNameById = (id) => carTypes.find(c => c.id === id)?.name || `Type ${id}`;

    // â”€â”€ Chip group for zone selection â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€
    const ZoneChips = ({ selected, onToggle, label, hint }) => (
        <Box>
            <Typography variant="subtitle2" gutterBottom>
                {label}
                {hint && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>{hint}</Typography>}
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.75}>
                {zones.map(zone => (
                    <Chip
                        key={zone.id}
                        label={zone.name}
                        onClick={() => onToggle(zone.id)}
                        color={selected.includes(zone.id) ? 'primary' : 'default'}
                        variant={selected.includes(zone.id) ? 'filled' : 'outlined'}
                        size="small"
                        sx={{ cursor: 'pointer' }}
                    />
                ))}
            </Box>
            {selected.length === 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    No zones selected.
                </Typography>
            )}
        </Box>
    );

    if (loading) {
        return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
    }

    return (
        <Box>
            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                <Box>
                    <Typography variant="h6">Rush Hour Fees</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Configure time-based surcharges. <strong>Zone-based</strong> fees fire when origin OR destination
                        is in a zone list. <strong>Route-based</strong> fees fire only for specific origin→destination
                        pairs. Both support per-car-type and per-day-of-week filters.
                        If any matching fee has "Overrides Others" set, the highest such fee wins;
                        otherwise the highest matching fee is charged.
                    </Typography>
                </Box>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ ml: 2, flexShrink: 0 }}>
                    Add Fee
                </Button>
            </Box>

            {fees.length === 0 ? (
                <Alert severity="info" icon={<AlarmIcon />}>
                    No rush hour fees configured. Rides will not receive any time-based surcharges.
                </Alert>
            ) : (
                <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell><strong>Name</strong></TableCell>
                                <TableCell><strong>Time Window</strong></TableCell>
                                <TableCell><strong>Fee</strong></TableCell>
                                <TableCell><strong>Zones / Route</strong></TableCell>
                                <TableCell><strong>Car Types</strong></TableCell>
                                <TableCell><strong>Days</strong></TableCell>
                                <TableCell align="center"><strong>Overrides</strong></TableCell>
                                <TableCell align="center"><strong>Status</strong></TableCell>
                                <TableCell align="right"><strong>Actions</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {fees.map((fee) => {
                                const isRoute = (fee.matchMode || 'ZONE') === 'ROUTE';
                                const zoneIds = parseIds(fee.zoneIds);
                                const originIds = parseIds(fee.originZoneIds);
                                const destIds = parseIds(fee.destinationZoneIds);
                                const ctIds = parseIds(fee.carTypeIds);
                                const dayIds = parseIds(fee.daysOfWeek);
                                return (
                                    <TableRow key={fee.id} hover sx={{ opacity: fee.isActive ? 1 : 0.5 }}>
                                        <TableCell><strong>{fee.name}</strong></TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={<AlarmIcon />}
                                                label={`${formatTimeOnly(fee.startTime)} — ${formatTimeOnly(fee.endTime)}`}
                                                size="small"
                                                variant="outlined"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip label={`$${fee.fee.toFixed(2)}`} size="small" color="success" />
                                        </TableCell>
                                        <TableCell>
                                            {isRoute ? (
                                                <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                                                    <Chip label="Route" size="small" color="secondary" />
                                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                                        {originIds.slice(0, 2).map(id => (
                                                            <Chip key={id} label={zoneNameById(id)} size="small" variant="outlined" />
                                                        ))}
                                                        {originIds.length > 2 && <Chip label={`+${originIds.length - 2}`} size="small" />}
                                                    </Box>
                                                    {/* {console.log(fee.name, 'is', fee.bidirectional ? 'bidirectional' : 'one direction')} */}

                                                    <Chip
                                                        icon={fee.bidirectional ? <SwapIcon /> : <ArrowForwardIcon />}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                    <Box display="flex" gap={0.5} flexWrap="wrap">
                                                        {destIds.slice(0, 2).map(id => (
                                                            <Chip key={id} label={zoneNameById(id)} size="small" variant="outlined" />
                                                        ))}
                                                        {destIds.length > 2 && <Chip label={`+${destIds.length - 2}`} size="small" />}
                                                    </Box>
                                                </Box>
                                            ) : (
                                                <Box display="flex" gap={0.5} flexWrap="wrap">
                                                    {zoneIds.length === 0
                                                        ? <Chip label="All Zones" size="small" color="primary" />
                                                        : zoneIds.slice(0, 3).map(id => (
                                                            <Chip key={id} label={zoneNameById(id)} size="small" variant="outlined" />
                                                        ))}
                                                    {zoneIds.length > 3 && (
                                                        <Chip label={`+${zoneIds.length - 3} more`} size="small" />
                                                    )}
                                                </Box>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {ctIds.length === 0
                                                ? <Chip label="All" size="small" color="default" />
                                                : <Box display="flex" gap={0.5} flexWrap="wrap">
                                                    {ctIds.slice(0, 3).map(id => (
                                                        <Chip key={id} label={carTypeNameById(id)} size="small" variant="outlined" />
                                                    ))}
                                                    {ctIds.length > 3 && <Chip label={`+${ctIds.length - 3} more`} size="small" />}
                                                </Box>}
                                        </TableCell>
                                        <TableCell>
                                            {dayIds.length === 0 || dayIds.length === 7
                                                ? <Chip label="Every day" size="small" color="default" />
                                                : <Typography variant="caption">{dayIds.sort().map(d => DAY_LABELS[d]).join(', ')}</Typography>}
                                        </TableCell>
                                        <TableCell align="center">
                                            {fee.overridesOthers
                                                ? <Tooltip title="Overrides other fees"><ActiveIcon color="warning" /></Tooltip>
                                                : <Tooltip title="Does not override"><InactiveIcon color="disabled" /></Tooltip>}
                                        </TableCell>
                                        <TableCell align="center">
                                            <Chip
                                                label={fee.isActive ? 'Active' : 'Inactive'}
                                                size="small"
                                                color={fee.isActive ? 'success' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Edit">
                                                <IconButton size="small" onClick={() => openEdit(fee)}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <IconButton size="small" color="error" onClick={() => setDeleteConfirm(fee)}>
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Create / Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{editing ? `Edit: ${editing.name}` : 'Add Rush Hour Fee'}</DialogTitle>
                <DialogContent dividers>
                    <Box display="flex" flexDirection="column" gap={2.5} pt={1}>
                        <TextField
                            fullWidth
                            label="Fee Name"
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="e.g. NYC Rush Hour"
                            required
                        />

                        <Box display="flex" gap={2}>
                            <TextField
                                label="Start Time"
                                type="time"
                                value={form.startTime}
                                onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                            />
                            <TextField
                                label="End Time"
                                type="time"
                                value={form.endTime}
                                onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                                slotProps={{ inputLabel: { shrink: true } }}
                                fullWidth
                            />
                        </Box>

                        <TextField
                            fullWidth
                            label="Surcharge Amount ($)"
                            type="number"
                            value={form.fee}
                            onChange={(e) => setForm({ ...form, fee: e.target.value })}
                            InputProps={{
                                startAdornment: <InputAdornment position="start">$</InputAdornment>,
                                inputProps: { min: 0.01, step: 0.50 }
                            }}
                            required
                        />

                        {/* Match mode toggle */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Match Mode</Typography>
                            <ToggleButtonGroup
                                value={form.matchMode}
                                exclusive
                                onChange={(_, val) => { if (val) setForm({ ...form, matchMode: val }); }}
                                size="small"
                            >
                                <ToggleButton value="ZONE">Zone-based</ToggleButton>
                                <ToggleButton value="ROUTE">Route-based</ToggleButton>
                            </ToggleButtonGroup>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                {form.matchMode === 'ZONE'
                                    ? 'Fires when origin OR destination is in the selected zones.'
                                    : 'Fires only when origin matches the origin zones AND destination matches the destination zones.'}
                            </Typography>
                        </Box>

                        {/* Zone selectors */}
                        {form.matchMode === 'ZONE' ? (
                            <ZoneChips
                                selected={selectedZoneIds}
                                onToggle={(id) => toggleId(setSelectedZoneIds, id)}
                                label="Applicable Zones"
                                hint="(leave empty to apply to ALL zones)"
                            />
                        ) : (
                            <>
                                <ZoneChips
                                    selected={selectedOriginZoneIds}
                                    onToggle={(id) => toggleId(setSelectedOriginZoneIds, id)}
                                    label="Origin Zones"
                                    hint="(required)"
                                />
                                <ZoneChips
                                    selected={selectedDestZoneIds}
                                    onToggle={(id) => toggleId(setSelectedDestZoneIds, id)}
                                    label="Destination Zones"
                                    hint="(required)"
                                />
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.bidirectional}
                                            onChange={(e) => setForm({ ...form, bidirectional: e.target.checked })}
                                        />
                                    }
                                    label="Bidirectional (also fires for the reversed route)"
                                />
                            </>
                        )}

                        {/* Car Types filter */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Car Types
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    (leave all unselected to apply to ALL car types)
                                </Typography>
                            </Typography>
                            <Box display="flex" flexWrap="wrap" gap={0.75}>
                                {carTypes.map(ct => (
                                    <Chip
                                        key={ct.id}
                                        label={ct.name}
                                        onClick={() => toggleId(setSelectedCarTypeIds, ct.id)}
                                        color={selectedCarTypeIds.includes(ct.id) ? 'primary' : 'default'}
                                        variant={selectedCarTypeIds.includes(ct.id) ? 'filled' : 'outlined'}
                                        size="small"
                                        sx={{ cursor: 'pointer' }}
                                    />
                                ))}
                            </Box>
                            {selectedCarTypeIds.length === 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    No car types selected — applies to all car types.
                                </Typography>
                            )}
                        </Box>

                        {/* Days of Week filter */}
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>
                                Days of Week
                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                    (leave all unselected to apply every day)
                                </Typography>
                            </Typography>
                            <Box display="flex" gap={0.75} flexWrap="wrap">
                                {DAY_LABELS.map((label, idx) => (
                                    <Chip
                                        key={idx}
                                        label={label}
                                        onClick={() => toggleId(setSelectedDays, idx)}
                                        color={selectedDays.includes(idx) ? 'primary' : 'default'}
                                        variant={selectedDays.includes(idx) ? 'filled' : 'outlined'}
                                        size="small"
                                        sx={{ cursor: 'pointer' }}
                                    />
                                ))}
                            </Box>
                            {selectedDays.length === 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                    No days selected — applies every day.
                                </Typography>
                            )}
                        </Box>

                        <Box>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={form.overridesOthers}
                                        onChange={(e) => setForm({ ...form, overridesOthers: e.target.checked })}
                                    />
                                }
                                label="Overrides other fees"
                            />
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ ml: 4 }}>
                                When on, this fee prevents stacking. If multiple overriding fees match,
                                only the highest one is charged.
                            </Typography>
                        </Box>

                        <FormControlLabel
                            control={
                                <Switch
                                    checked={form.isActive}
                                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                                />
                            }
                            label="Active"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleSave} disabled={saving}>
                        {saving ? 'Saving...' : editing ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirm */}
            <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
                <DialogTitle>Delete Rush Hour Fee</DialogTitle>
                <DialogContent>
                    <Typography>
                        Delete <strong>{deleteConfirm?.name}</strong>? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
                    <Button color="error" variant="contained" onClick={() => handleDelete(deleteConfirm)}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default RushHourFeeManager;
